import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { connect } from 'react-redux';
import { Row, Col, Collapse, Button } from 'reactstrap';
import { AppraisalReportStepPriceTitle } from 'client/site-modules/shared/components/appraisal/appraisal-report-price-title/appraisal-report-price-title';
import { TRADE_IN_VALUE } from 'site-modules/shared/constants/appraisal/appraisal';
import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import {
  calculateLimits,
  getAppLiedLimitAmount,
  getValidationFormMessages,
} from 'client/site-modules/shared/utils/calculator/calculator-helper';
import { LIMIT_TRADE_IN, NEGATIVE_TRADE_IN_MSG } from 'site-modules/shared/constants/calculator/calculator';
import { PUB_STATES } from 'client/constants/pub-states';
import { AppraisalReportStepPayoffAmount } from './appraisal-report-step-payoff-amount';
import { AppraisalReportStepTotalTradeIn } from './appraisal-report-step-total-trade-in';
import { AppraisalReportStepAppliedTradeIn } from './appraisal-report-step-applied-trade-in';

function getPrice(price) {
  const tradeInPrice = price && price[TRADE_IN_VALUE];
  return getPriceStringV2(tradeInPrice, '$0');
}

/**
 * Renders price items.
 * @param {String} title
 * @param {Number} [value]
 * @param {Object[]} [values]
 * @param {String} [info]
 * @returns {ReactElement}
 */
export function AppraisalReportStepPriceItem({ title, value = {}, values = [], info = '' }) {
  const priceValue = value[TRADE_IN_VALUE];

  return (
    <Fragment>
      <Row className="mb-0_5 mt-0_75">
        <Col xs={9} md={9} lg={9}>
          <AppraisalReportStepPriceTitle title={title} info={info} id={makeNiceName(title)} />
        </Col>
        <Col xs={3} className="text-end">
          {getPriceStringV2(priceValue, '$0')}
        </Col>
        {!!values.length &&
          values.map(({ name, price }) => (
            <Fragment key={name}>
              <Col
                xs={9}
                className={classNames('mt-0_75 ps-1_5', { 'option-names-selections': title === 'Optional Equipment' })}
              >
                {name}
              </Col>
              <Col xs={3} className="mt-0_75 text-end">
                {getPrice(price)}
              </Col>
            </Fragment>
          ))}
      </Row>
    </Fragment>
  );
}

AppraisalReportStepPriceItem.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.shape({}),
  values: PropTypes.arrayOf(PropTypes.shape({})),
  info: PropTypes.string,
};

export function AppraisalReportStepPricesUI({
  totalPrice,
  priceList = [],
  onPayoffChange,
  onPaymentLimitsLoading,
  sellingPrice = 0,
  msrp = 0,
  residualValue = 0,
  isPurchase = false,
  isUsed = false,
  styleId = null,
  zipCode = '',
  pageName = '',
  venomVersion = '',
  displayTradeInValues = true,
}) {
  const [isPriceTableOpen, setIsPriceTableOpen] = useState(false);
  const [totalTradeIn, setTotalTradeIn] = useState(totalPrice);
  const [appliedTradeIn, setAppliedTradeIn] = useState(0);
  const [payoffAmount, setPayoffAmount] = useState(0);
  const [infoMessages, setInfoMessages] = useState({ totalTradeInMsg: '', appliedTradeInMsg: '' });
  const requestIdRef = useRef(0);

  const fetchLimitsAndUpdate = useCallback(
    async (tradeIn, newPayoffAmount) => {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      onPaymentLimitsLoading(true);

      try {
        const limits = await calculateLimits({
          salesPrice: sellingPrice,
          msrp,
          residual: residualValue,
          isPurchase,
          isUsed,
          styleId,
          zipCode,
          tradeIn,
          pageName,
          venomVersion,
        });

        // Avoid race condition when multiple payoff changes happen in a short time
        if (requestId !== requestIdRef.current) return;

        const publicationState = isUsed ? PUB_STATES.USED : PUB_STATES.NEW;
        const amountLimit = getAppLiedLimitAmount(limits, LIMIT_TRADE_IN) || tradeIn;
        const { tradeInValidationError } = getValidationFormMessages(
          get(limits, 'limitCriteria', {}),
          publicationState
        );

        onPayoffChange(newPayoffAmount, amountLimit);

        setInfoMessages({
          totalTradeInMsg: tradeIn < 0 ? NEGATIVE_TRADE_IN_MSG : '',
          appliedTradeInMsg: tradeInValidationError,
        });
        setAppliedTradeIn(amountLimit);
      } finally {
        // Only clear loading for the latest request to avoid prematurely
        // enabling the button while a newer request is still in-flight.
        if (requestId === requestIdRef.current) {
          onPaymentLimitsLoading(false);
        }
      }
    },
    [
      isPurchase,
      isUsed,
      msrp,
      onPaymentLimitsLoading,
      onPayoffChange,
      pageName,
      residualValue,
      sellingPrice,
      styleId,
      venomVersion,
      zipCode,
    ]
  );

  useEffect(
    () => {
      fetchLimitsAndUpdate(totalPrice, payoffAmount);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const toggleDetailedReport = useCallback(() => setIsPriceTableOpen(isOpen => !isOpen), []);
  const handlePayoffInputChange = useCallback(newPayoffAmount => setPayoffAmount(newPayoffAmount), []);
  const handlePayoffChange = useCallback(
    (newPayoffAmount = 0) => {
      const newTotalTradeIn = totalPrice + newPayoffAmount;
      setPayoffAmount(newPayoffAmount);
      setTotalTradeIn(newTotalTradeIn);
      fetchLimitsAndUpdate(newTotalTradeIn, newPayoffAmount);
    },
    [totalPrice, fetchLimitsAndUpdate]
  );

  return (
    <div>
      <Button
        color="link"
        block
        data-tracking-id="view_detailed_report"
        className="m-0 p-0 mt-0_75 text-decoration-none text-start heading-6 fw-normal"
        onClick={toggleDetailedReport}
      >
        <span className="text-primary-darker">
          <span>See Detailed Report</span>
          <span
            className={classNames('small ms-0_25', {
              'icon-arrow-up3': isPriceTableOpen,
              'icon-arrow-down3': !isPriceTableOpen,
            })}
            aria-hidden
          />
        </span>
      </Button>
      <Collapse isOpen={isPriceTableOpen} className="heading-5 fw-normal">
        {priceList.map(price => (
          <AppraisalReportStepPriceItem key={`appraisal report - ${price.title}`} {...price} />
        ))}
      </Collapse>
      {displayTradeInValues && (
        <>
          <hr className="my-1_5 my-md-1" />
          <AppraisalReportStepPayoffAmount
            value={payoffAmount}
            onChange={handlePayoffInputChange}
            onPayoffChange={handlePayoffChange}
          />
          <hr className="my-1_5 my-md-1" />

          <AppraisalReportStepTotalTradeIn totalTradeIn={totalTradeIn} infoMessage={infoMessages.totalTradeInMsg} />
          <AppraisalReportStepAppliedTradeIn
            appliedTradeIn={appliedTradeIn}
            infoMessage={infoMessages.appliedTradeInMsg}
          />
        </>
      )}
    </div>
  );
}

AppraisalReportStepPricesUI.propTypes = {
  totalPrice: PropTypes.number.isRequired,
  priceList: PropTypes.arrayOf(PropTypes.shape({})),
  onPayoffChange: PropTypes.func.isRequired,
  onPaymentLimitsLoading: PropTypes.func.isRequired,
  sellingPrice: PropTypes.number,
  msrp: PropTypes.number,
  residualValue: PropTypes.number,
  isPurchase: PropTypes.bool,
  isUsed: PropTypes.bool,
  styleId: PropTypes.number,
  zipCode: PropTypes.string,
  venomVersion: PropTypes.string,
  pageName: PropTypes.string,
  displayTradeInValues: PropTypes.bool,
};

export const mapStateToProps = state => ({
  venomVersion: get(state, 'venomVersion.version'),
  pageName: get(state, 'pageContext.page.name'),
});

export const AppraisalReportStepPrices = connect(mapStateToProps)(AppraisalReportStepPricesUI);
